import {
  Datagrid,
  List,
  Create,
  Edit,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  SelectInput,
  BooleanField,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Chip, Box } from '@mui/material';
import { ArchivalToolbar } from '../components/ArchivalToolbar';
import { ArchivedSelect } from '../components/selects';
import { ScopePicker } from '../components/ScopePicker';
import { hasRole, type Permissions } from '../types';
import { useController } from 'react-hook-form';

// ---------------------------------------------------------------------------
// Echelon type choices
// ---------------------------------------------------------------------------

const ECHELON_CHOICES = [
  { id: 'Command', name: 'Command' },
  { id: 'Wing', name: 'Wing' },
  { id: 'Group', name: 'Group' },
  { id: 'Squadron', name: 'Squadron' },
  { id: 'Flight', name: 'Flight' },
  { id: 'Team', name: 'Team' },
  { id: 'Brigade', name: 'Brigade' },
  { id: 'Battalion', name: 'Battalion' },
  { id: 'Company', name: 'Company' },
  { id: 'Division', name: 'Division' },
  { id: 'other', name: 'Other' },
];

// ---------------------------------------------------------------------------
// Shared display helpers
// ---------------------------------------------------------------------------

const ArchivedChip = () => {
  const record = useRecordContext();
  if (!record) return null;
  return record.archived_at ? (
    <Chip label="Archived" size="small" color="warning" />
  ) : (
    <Chip label="Active" size="small" color="success" />
  );
};
ArchivedChip.displayName = 'ArchivedChip';

const EchelonChip = () => {
  const record = useRecordContext();
  if (!record?.echelonType) return null;
  return <Chip label={record.echelonType} size="small" variant="outlined" />;
};
EchelonChip.displayName = 'EchelonChip';

// ---------------------------------------------------------------------------
// ScopePicker as a react-hook-form-connected input
// ---------------------------------------------------------------------------

interface ScopePickerInputProps {
  source: string;
  label?: string;
  disabled?: boolean;
}

const ScopePickerInput = ({ source, label, disabled }: ScopePickerInputProps) => {
  const { field } = useController({ name: source, defaultValue: '' });
  return (
    <ScopePicker
      value={field.value ?? ''}
      onChange={field.onChange}
      label={label}
      disabled={disabled}
    />
  );
};
ScopePickerInput.displayName = 'ScopePickerInput';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const OrgUnitList = () => {
  const { permissions } = usePermissions<Permissions>();
  const isAdmin = hasRole(permissions, 'tenant_admin');

  return (
    <List
      exporter={false}
      filters={[<ArchivedSelect key="archived" />]}
      sort={{ field: 'path', order: 'ASC' }}
      perPage={25}
    >
      <Datagrid rowClick={isAdmin ? 'edit' : 'show'}>
        <TextField source="path" label="Path" />
        <TextField source="name" label="Name" />
        <EchelonChip />
        <TextField source="parentPath" label="Parent Path" />
        <ArchivedChip />
      </Datagrid>
    </List>
  );
};

// ---------------------------------------------------------------------------
// Show
// ---------------------------------------------------------------------------

export const OrgUnitShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="path" label="Path" />
      <TextField source="name" label="Name" />
      <TextField source="echelonType" label="Echelon Type" />
      <TextField source="parentPath" label="Parent Path" />
      <BooleanField source="archived_at" label="Archived" />
    </SimpleShowLayout>
  </Show>
);

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const OrgUnitCreate = () => (
  <Create>
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <TextInput fullWidth source="path" label="Path" helperText="e.g. /aetc/2af" />
      <TextInput fullWidth source="name" label="Name" />
      <SelectInput
        source="echelonType"
        label="Echelon Type"
        choices={ECHELON_CHOICES}
        fullWidth
      />
      <Box mb={1}>
        <ScopePickerInput source="parentPath" label="Parent Path" />
      </Box>
      <TextInput
        fullWidth
        source="taxonomyRefs"
        label="Taxonomy Refs (comma-separated)"
        helperText="Enter taxonomy reference codes separated by commas"
        parse={(v: string) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : [])}
        format={(v: string[]) => (Array.isArray(v) ? v.join(', ') : v ?? '')}
      />
    </SimpleForm>
  </Create>
);

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

export const OrgUnitEdit = () => (
  <Edit mutationMode="pessimistic">
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <TextInput fullWidth source="path" label="Path" disabled helperText="Path is immutable after creation" />
      <TextInput fullWidth source="name" label="Name" />
      <SelectInput
        source="echelonType"
        label="Echelon Type"
        choices={ECHELON_CHOICES}
        fullWidth
      />
      <Box mb={1}>
        <ScopePickerInput source="parentPath" label="Parent Path" />
      </Box>
      <TextInput
        fullWidth
        source="taxonomyRefs"
        label="Taxonomy Refs (comma-separated)"
        helperText="Enter taxonomy reference codes separated by commas"
        parse={(v: string) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : [])}
        format={(v: string[]) => (Array.isArray(v) ? v.join(', ') : v ?? '')}
      />
    </SimpleForm>
  </Edit>
);

// ---------------------------------------------------------------------------
// Named export object (matches pattern used by other resources)
// ---------------------------------------------------------------------------

export const org_units = {
  list: OrgUnitList,
  show: OrgUnitShow,
  create: OrgUnitCreate,
  edit: OrgUnitEdit,
};
